import { useEffect, useState, type FC } from "react";
import { IntervalToggleContainer } from "../CronGenerator.style";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import React from "react";

type CronValues = {
    minute: string;
    hour: string;
    day: string;
    month: string;
    weekDay: string;
};

type MonthProps = {
    setCronValues: React.Dispatch<React.SetStateAction<CronValues>>;
    selectedDay: string;
    clear: boolean;
};

const MonthOptions = {
    EVERY: "Month",
    RANGE: "Range",
} as const;

const maxDaysForMonth = (month: number): number => {
    if (month === 2) return 29;
    if ([4, 6, 9, 11].includes(month)) return 30;
    return 31;
};

const Month: FC<MonthProps> = ({ setCronValues, selectedDay, clear }) => {
    const [activeOption, setActiveOption] = useState<string>(MonthOptions.EVERY);
    const [month, setMonth] = useState("*");
    const [prefixInterval, setPrefixInterval] = useState("*");
    const [prefixError, setPrefixError] = useState(false);
    const [intervalError, setIntervalError] = useState(false);
    const [monthRange, setMonthRange] = useState({ from: "", to: "" });
    const [monthRangeError, setMonthRangeError] = useState({ fromError: false, toError: false });
    const [warning, setWarning] = useState("");
    const [startAtEnabled, setStartAtEnabled] = useState<boolean>(false);

    useEffect(() => {
        setMonth("*");
        setPrefixInterval("*");
        setPrefixError(false);
        setIntervalError(false);
        setMonthRange({ from: "", to: "" });
        setMonthRangeError({ fromError: false, toError: false });
        setWarning("");
        setStartAtEnabled(false)
    }, [activeOption, clear]);

    const handleOptionSelect = (event: SelectChangeEvent) => {
        setActiveOption(event.target.value);
    };

    const handleMonthChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setMonth("*");
            setIntervalError(false);
            setStartAtEnabled(false)
        } else if (val >= 1 && val <= 12) {
            setMonth(event.target.value);
            setIntervalError(false);
            setStartAtEnabled(true);
        } else {
            setMonth("*");
            setIntervalError(true);
            setStartAtEnabled(false);
        }
    };

    const handlePrefixChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        if (event.target.value === "") {
            setPrefixInterval("*");
            setPrefixError(false);
        } else if (val >= 1 && val <= 12) {
            setPrefixInterval(event.target.value);
            setPrefixError(false);
        } else {
            setPrefixInterval("*");
            setPrefixError(true);
        }
    };

    const handleFromChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        setMonthRange((prev) => ({ ...prev, from: event.target.value }));
        const toVal = Number(monthRange.to);
        const fromError = !(val >= 1 && val <= 12);
        if (fromError) {
            setStartAtEnabled(false);
        } else {
            setStartAtEnabled(true);
        }
        setMonthRangeError({
            fromError,
            toError: !(val <= toVal && toVal <= 12),
        });
    };

    const handleToChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const val = Number(event.target.value);
        setMonthRange((prev) => ({ ...prev, to: event.target.value }));
        const fromVal = Number(monthRange.from);
        setMonthRangeError((prev) => ({
            ...prev,
            toError: !(fromVal <= val && val <= 12),
        }));
    };

    useEffect(() => {
        if (activeOption !== MonthOptions.EVERY) return;

        const m = Number(month);
        const p = Number(prefixInterval);
        const d = Number(selectedDay);

        let value = "*";
        setWarning("");

        if (
            month !== "*" &&
            prefixInterval !== "*" &&
            !isNaN(m) &&
            !isNaN(p) &&
            m >= 1 &&
            m <= 12 &&
            p >= 1 &&
            p <= 12
        ) {
            if (d > maxDaysForMonth(m)) {
                setWarning(`Day ${d} is invalid for selected month ${m}.`);
            } else {
                value = `${prefixInterval}/${month}`;
            }
        } else if (month !== "*" && !isNaN(m) && m >= 1 && m <= 12) {
            if (d > maxDaysForMonth(m)) {
                setWarning(`Day ${d} is invalid for month ${m}.`);
            }
            value = month;

        }

        setCronValues((prev) => ({
            ...prev,
            month: value,
        }));
    }, [month, prefixInterval, selectedDay, activeOption, setCronValues]);

    useEffect(() => {
        if (activeOption !== MonthOptions.RANGE) {
            setCronValues((prev) => ({ ...prev, month: "*" }));
            return;
        }

        const from = Number(monthRange.from);
        const to = Number(monthRange.to);
        const d = Number(selectedDay);
        let value = "*";
        setWarning("");

        if (
            monthRange.from !== "" &&
            monthRange.to !== "" &&
            !isNaN(from) &&
            !isNaN(to) &&
            from >= 1 &&
            to <= 12 &&
            from <= to
        ) {
            // check if any month in range has max day less than selected day
            const invalid = Array.from({ length: to - from + 1 }, (_, i) => from + i).some(
                (m) => d > maxDaysForMonth(m)
            );

            if (invalid || !d) {
                value = `${from}-${to}`;
            }
        }

        setCronValues((prev) => ({
            ...prev,
            month: value,
        }));
    }, [monthRange, selectedDay, activeOption, setCronValues]);

    const renderSelector = () => {
        if (activeOption === MonthOptions.EVERY) {
            return (
                <>
                    <TextField
                        label="Months"
                        variant="standard"
                        sx={{ m: 1, width: "fit-content" }}
                        onChange={handleMonthChange}
                        error={intervalError}
                        helperText={intervalError ? "1 to 12" : `${warning || ""}`}
                        value={month === "*" ? "" : month}
                    />
                    <TextField
                        label="Start at"
                        variant="standard"
                        disabled={!startAtEnabled}
                        sx={{ m: 1, width: "fit-content" }}
                        onChange={handlePrefixChange}
                        error={prefixError}
                        helperText={prefixError ? "1 to 12" : "*Months"}
                        value={prefixInterval === "*" ? "" : prefixInterval}
                    />
                </>
            );
        }

        if (activeOption === MonthOptions.RANGE) {
            return (
                <>
                    <TextField
                        label="From"
                        variant="standard"
                        sx={{ m: 1, width: "fit-content" }}
                        value={monthRange.from}
                        onChange={handleFromChange}
                        error={monthRangeError.fromError}
                        helperText={monthRangeError.fromError ? "1 to 12" : " "}
                    />
                    <TextField
                        label="To"
                        variant="standard"
                        disabled={!startAtEnabled}
                        sx={{ m: 1, width: "fit-content" }}
                        value={monthRange.to}
                        onChange={handleToChange}
                        error={monthRangeError.toError}
                        helperText={
                            monthRangeError.toError
                                ? `${monthRange.from || "1"} to 12`
                                : " "
                        }
                    />
                </>
            );
        }

        return null;
    };

    return (
        <IntervalToggleContainer style={{ alignItems: "start" }}>
            <FormControl variant="standard" sx={{ m: 1, minWidth: 150 }}>
                <InputLabel id="month-select-label">Month</InputLabel>
                <Select
                    labelId="month-select-label"
                    value={activeOption}
                    onChange={handleOptionSelect}
                >
                    <MenuItem value={MonthOptions.EVERY}>{MonthOptions.EVERY}</MenuItem>
                    <MenuItem value={MonthOptions.RANGE}>{MonthOptions.RANGE}</MenuItem>
                </Select>
            </FormControl>
            {renderSelector()}
        </IntervalToggleContainer>
    );
};

export default Month;
